// useMediaExport - Hook for managing media exports (PDF + video)
// Uses server actions + SSE for real-time updates.
// Results appear in export history (prefetched via React Query).

'use client';

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { exportIllustrationPdf, startVideoExport as startVideoExportAction } from '@/contexts/illustrations';
import { getStoryExports } from '@/contexts/media/actions/getStoryExports';
import { mediaKeys } from '@/contexts/media/queryKeys';
import type { StoryExport } from '@/contexts/media/actions/getStoryExports';

interface UseMediaExportOptions {
  bookId: string;
  storyId: string;
  audioStoryId?: string;
}

interface UseMediaExportReturn {
  pdfLoading: boolean;
  pdfProgress: number | null;
  pdfError: string | null;
  /** True when the last HD export was rejected for lack of allowance → show upsell. */
  pdfInsufficientAllowance: boolean;
  exportPdf: (
    imagesOnly?: boolean,
    hd?: boolean,
    paper?: 'trim' | 'a4',
    edgeFill?: boolean,
    archive?: boolean,
  ) => Promise<void>;

  videoLoading: boolean;
  videoProgress: number | null;
  videoUrl: string | null;
  videoError: string | null;
  /** Vertical (phone) by default; pass 'horizontal' for the old landscape film. */
  /**
   * `silent`: the user chose a video without narration (the dialog said «видео будет немым») —
   * no audio story goes to the export, whatever narration exists (dev run 7, V1).
   */
  startVideoExport: (orientation?: 'vertical' | 'horizontal', opts?: { silent?: boolean }) => Promise<void>;

  existingExports: StoryExport[];
}

export function useMediaExport({ bookId, storyId, audioStoryId }: UseMediaExportOptions): UseMediaExportReturn {
  // Prefetched existing exports — SSE events invalidate this query on completion
  const { data: existingExportsResult } = useQuery({
    queryKey: mediaKeys.exports(storyId),
    queryFn: () => getStoryExports(storyId),
    // SSE (media.export.*) invalidates this on completion; 30s matches default.
    staleTime: 30_000,
  });

  const existingExports: StoryExport[] =
    existingExportsResult?.success ? existingExportsResult.data : [];

  // Derive latest video URL from existing exports (for BookViewer inline playback)
  const latestVideo = existingExports.find(e => e.exportType === 'video');
  const videoUrl = latestVideo?.resultUrl ?? null;

  const queryClient = useQueryClient();

  // PDF state
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<number | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfInsufficientAllowance, setPdfInsufficientAllowance] = useState(false);
  const [pdfJobId, setPdfJobId] = useState<string | null>(null);

  // Video state
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoJobId, setVideoJobId] = useState<string | null>(null);

  // Restore in-progress export state from DB on mount
  useEffect(() => {
    if (!existingExports.length) return;
    const inProgressStatuses = ['pending', 'downloading', 'processing', 'uploading'];

    const activeVideo = existingExports.find(
      e => e.exportType === 'video' && inProgressStatuses.includes(e.status)
    );
    if (activeVideo && !videoJobId) {
      setVideoLoading(true);
      setVideoJobId(activeVideo.id);
      setVideoProgress(activeVideo.progress ?? null);
    }

    const activePdf = existingExports.find(
      e => (e.exportType === 'pdf' || e.exportType === 'zip') && inProgressStatuses.includes(e.status)
    );
    if (activePdf && !pdfJobId) {
      setPdfLoading(true);
      setPdfJobId(activePdf.id);
      setPdfProgress(activePdf.progress ?? null);
    }
  }, [existingExports]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync loading state from DB — fallback when SSE events are lost
  useEffect(() => {
    if (!existingExports.length) return;

    if (videoJobId) {
      const job = existingExports.find(e => e.id === videoJobId);
      if (job) {
        if (job.status === 'completed' || job.status === 'failed') {
          setVideoLoading(false);
          setVideoJobId(null);
          setVideoProgress(null);
          if (job.status === 'failed') setVideoError('VIDEO_GENERATION_FAILED');
        } else if (typeof job.progress === 'number') {
          setVideoProgress(job.progress);
        }
      }
    }

    if (pdfJobId) {
      const job = existingExports.find(e => e.id === pdfJobId);
      if (job) {
        if (job.status === 'completed' || job.status === 'failed') {
          setPdfLoading(false);
          setPdfJobId(null);
          setPdfProgress(null);
          if (job.status === 'failed') setPdfError('PDF_EXPORT_FAILED');
        } else if (typeof job.progress === 'number') {
          setPdfProgress(job.progress);
        }
      }
    }
  }, [existingExports, videoJobId, pdfJobId]);

  // SSE: listen for export progress
  useEventSourceEvent('media.export.progress', useCallback((payload: Record<string, unknown>) => {
    if (payload.jobId === videoJobId) {
      setVideoProgress(payload.progress as number);
    } else if (payload.jobId === pdfJobId) {
      setPdfProgress(payload.progress as number);
    }
  }, [videoJobId, pdfJobId]));

  // SSE: listen for export completion → refresh history
  useEventSourceEvent('media.export.completed', useCallback((payload: Record<string, unknown>) => {
    if (payload.jobId === videoJobId) {
      setVideoLoading(false);
      setVideoJobId(null);
      setVideoProgress(null);
      queryClient.invalidateQueries({ queryKey: mediaKeys.exports(storyId) });
    } else if (payload.jobId === pdfJobId) {
      setPdfLoading(false);
      setPdfJobId(null);
      setPdfProgress(null);
      queryClient.invalidateQueries({ queryKey: mediaKeys.exports(storyId) });
    }
  }, [videoJobId, pdfJobId, queryClient, storyId]));

  // SSE: listen for export failure
  useEventSourceEvent('media.export.failed', useCallback((payload: Record<string, unknown>) => {
    if (payload.jobId === videoJobId) {
      setVideoError((payload.error as string) || 'VIDEO_GENERATION_FAILED');
      setVideoLoading(false);
      setVideoJobId(null);
    } else if (payload.jobId === pdfJobId) {
      setPdfError((payload.error as string) || 'PDF_EXPORT_FAILED');
      setPdfLoading(false);
      setPdfJobId(null);
      setPdfProgress(null);
    }
  }, [videoJobId, pdfJobId]));

  // PDF export (hd=true → print-DPI upscale, debits allowance)
  const handleExportPdf = useCallback(async (
    imagesOnly?: boolean,
    hd?: boolean,
    paper?: 'trim' | 'a4',
    edgeFill?: boolean,
    archive?: boolean,
  ) => {
    if (pdfLoading) return;

    setPdfLoading(true);
    setPdfError(null);
    setPdfInsufficientAllowance(false);
    setPdfProgress(null);

    try {
      const result = await exportIllustrationPdf({
        bookId, storyId, imagesOnly: imagesOnly ?? false, hd: hd ?? false, paper, edgeFill, archive,
      });

      if (result.success) {
        if (result.data.jobId) {
          setPdfJobId(result.data.jobId);
        } else {
          setPdfLoading(false);
        }
      } else {
        // Out-of-allowance on an HD export → upsell, not a raw error banner.
        if (result.code === 'INSUFFICIENT_ALLOWANCE') {
          setPdfInsufficientAllowance(true);
        } else {
          setPdfError(result.error || 'PDF_EXPORT_FAILED');
        }
        setPdfLoading(false);
      }
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : 'PDF_EXPORT_FAILED');
      setPdfLoading(false);
    }
  }, [bookId, storyId, pdfLoading]);

  // Video export
  const handleStartVideoExport = useCallback(async (orientation?: 'vertical' | 'horizontal', opts?: { silent?: boolean }) => {
    if (videoLoading) return;

    setVideoLoading(true);
    setVideoError(null);
    setVideoProgress(null);

    try {
      const result = await startVideoExportAction(bookId, storyId, opts?.silent ? undefined : audioStoryId, orientation);

      if (result.success) {
        if (result.data.jobId) {
          setVideoJobId(result.data.jobId);
        } else {
          setVideoLoading(false);
        }
      } else {
        setVideoError(result.error || 'VIDEO_EXPORT_FAILED');
        setVideoLoading(false);
      }
    } catch (error) {
      setVideoError(error instanceof Error ? error.message : 'VIDEO_EXPORT_FAILED');
      setVideoLoading(false);
    }
  }, [bookId, storyId, audioStoryId, videoLoading]);

  return {
    pdfLoading,
    pdfProgress,
    pdfError,
    pdfInsufficientAllowance,
    exportPdf: handleExportPdf,
    videoLoading,
    videoProgress,
    videoUrl,
    videoError,
    startVideoExport: handleStartVideoExport,
    existingExports,
  };
}
